import type { Moment } from 'moment';
import { toMoment } from '@shared/utils/date';
import HIIHireAddressCommission from '@shared/hireInvoice/HIIHireAddressCommission';
import HIIBallastBonus from '@shared/hireInvoice/HIIBallastBonus';
import HIIBallastBonusAddressCommission from '@shared/hireInvoice/HIIBallastBonusAddressCommission';
import HIIBallastBonusBrokerCommission from '@shared/hireInvoice/HIIBallastBonusBrokerCommission';
import HIIHireBrokerCommission from '@shared/hireInvoice/HIIHireBrokerCommission';
import HIIHirePeriod from '@shared/hireInvoice/HIIHirePeriod';
import HIIExpenseSubjectToHireDays from '@shared/hireInvoice/HIIExpenseSubjectToHireDays';
import HIIRevenueSubjectToDays from '@shared/hireInvoice/HIIRevenueSubjectToDays';
import type HIIVoyageExpenseModel from '@api/models/hii-voyage-expense';
import HIIHirePeriodModel from '@api/models/hii-hire-period';
import type HireInvoiceItemModel from '@api/models/hire-invoice-item';
import type { ExpenseSubjectToHireDaysProps } from '@api/models/expense-subject-to-hire-days';
import type { BankAccountProps } from '@api/models/bank-account';
import type { VoyageOffHirePeriodProps } from '@api/models/voyage-off-hire-period';
import type { VoyageExpenseReceivableProps } from '@api/models/voyage-expense-receivable';
import type { BunkerProps } from '@api/models/bunker';
import type { VoyageOffHireBunkerProps } from '@api/models/voyage-off-hire-bunker';
import { RevenueSubjectToDaysProps } from '@api/models/revenue-subject-to-days';
import { OffHirePeriodProps } from '@api/models/off-hire-period';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import { PeriodSchedule } from '@api/features/fixtures/getFixtureDetails';
import { VoyageBunkerEntry } from '@api/utils/sequelize/getVoyageBunkers';
import {
	getPendingAmendmentItems,
	isUninvoicedSourceItem,
	linkChildrenToParents,
} from './getPendingAmendmentItems';
import type { Values } from './objectEnums';
import {
	AccountTypes,
	CalculationMethodForExpensesSubjectToHireDays,
	FixtureTypes,
	HireInvoiceItemStates,
	HireTypes,
	HireUnit,
} from './constants';
import {
	getExpenseHireInvoiceItem,
	getBunkerHireInvoiceItem,
	getOffHireHireInvoiceItem,
} from './hireInvoice';
import getPeriodFromDate, { getPeriodFromDateForHire } from './getPeriodFromDate';

let _id = -1;

const getId = () => {
	const newId = _id;

	_id -= 1;

	return newId;
};

export type WithHIIState<T> = T & { state: Values<typeof HireInvoiceItemStates> };

export type TcParams = {
	isHireInvoice: boolean;
	commencementDate: Moment | string | null;
	completionDate: Moment | string | null;
	hireInvoices: GetVoyageDetailsResponse['hireInvoices'];
	daysPerHirePeriod: number;
	invoicePerCalenderMonth: boolean | null;
	periodSchedule: PeriodSchedule[];
	hireDaysDecimals: number;
	hireDescription: string | null;
	expensesSubjectToHireDays: ExpenseSubjectToHireDaysProps[];
	revenuesSubjectToDays: RevenueSubjectToDaysProps[];
	calculationMethodForExpensesSubjectToHireDays: Values<typeof CalculationMethodForExpensesSubjectToHireDays> | null;
	addressCommission: number;
	brokers: Array<{
		id: number;
		name: string;
		commission: number | null;
		paidBy: Values<typeof AccountTypes>;
	}>;
	grossBallastBonus: number | null;
	ballastBonusAvailable: boolean;
	readyForCumulative: boolean;
	voyageBunkers: VoyageBunkerEntry[];
	voyageExpenseReceivables: Array<
		WithHIIState<VoyageExpenseReceivableProps> & {
			HIIVoyageExpense: HIIVoyageExpenseModel & {
				HireInvoiceItem: HireInvoiceItemModel;
			};
		}
	>;
	voyageOffHirePeriods: Array<WithHIIState<VoyageOffHirePeriodProps & OffHirePeriodProps> & {
		bunkers: Array<VoyageOffHireBunkerProps & BunkerProps>;
		HIIOffHirePeriod: HIIHirePeriodModel & {
			HireInvoiceItem: HireInvoiceItemModel;
		};
	}>;
	bankAccount: BankAccountProps;
	type: typeof FixtureTypes.TC_OUT | typeof FixtureTypes.TC_IN | typeof FixtureTypes.BB_OUT;
	useUTC: boolean;
	hireUnit: HireUnit | undefined;
	useLegacyTime: boolean;
	amendments: GetVoyageDetailsResponse['amendments']
};

const getAvailableHireInvoiceItems = (params: TcParams) => {
	const {
		isHireInvoice,
		brokers: allBrokers,
		hireInvoices,
		bankAccount,
		addressCommission,
		commencementDate,
		completionDate,
		daysPerHirePeriod,
		invoicePerCalenderMonth,
		periodSchedule,
		hireDescription: fixtureHireDescription,
		expensesSubjectToHireDays,
		revenuesSubjectToDays,
		calculationMethodForExpensesSubjectToHireDays,
		grossBallastBonus,
		ballastBonusAvailable,
		readyForCumulative,
		voyageBunkers,
		voyageExpenseReceivables,
		voyageOffHirePeriods,
		useUTC,
		hireDaysDecimals,
		hireUnit,
		amendments,
		useLegacyTime,
	} = params;

	const items = [];

	const brokers = allBrokers.filter((b) => b.paidBy === AccountTypes.CHARTERER);

	if (commencementDate == null) {
		return [];
	}

	let fromDate = commencementDate;

	const commencementPeriod = getPeriodFromDate(toMoment(fromDate), periodSchedule);
	let hireRate = commencementPeriod?.hireRate ?? 0;

	let hireDescription = fixtureHireDescription;
	let hireType = commencementPeriod?.hireType ?? HireTypes.FIXED;

	const hireInvoiceItems = hireInvoices.flatMap((i) => i.HireInvoiceItems);

	const previousHireItems = hireInvoiceItems.reduce((arr: HIIHirePeriodModel[], item) => {
		if (item.HIIHirePeriod == null) {
			return arr;
		}

		if (item.isReversal) {
			return arr;
		}

		const period = item.HIIHirePeriod;

		return [...arr, period];
	}, []).sort((a, b) => {
		const fromDiff = toMoment(a.from).diff(b.from);

		if (fromDiff !== 0) {
			return fromDiff;
		}

		// When from dates are equal (e.g. original + amendment for the same period),
		// prefer the newer item so that an amended `to` date takes precedence.
		return a.hireInvoiceItemId - b.hireInvoiceItemId;
	});

	if (previousHireItems.length > 0 && !readyForCumulative) {
		const latestHireItem = previousHireItems[previousHireItems.length - 1];
		fromDate = toMoment(latestHireItem.to);
		hireDescription = (latestHireItem?.hireDescription ?? '');

		const periodForFromDate = getPeriodFromDateForHire(toMoment(fromDate), periodSchedule);

		if (periodForFromDate != null) {
			hireRate = periodForFromDate.hireRate;
			hireType = periodForFromDate.hireType;
		} else {
			hireRate = latestHireItem.hireRate;
			hireType = latestHireItem?.hireType as Values<typeof HireTypes>;
		}
	}

	fromDate = toMoment(fromDate).seconds(0).milliseconds(0);

	let daysToAdd = daysPerHirePeriod;

	if (invoicePerCalenderMonth) {
		daysToAdd = fromDate.daysInMonth();
	}

	let toDate = fromDate.clone().add(daysToAdd, 'days').seconds(0).milliseconds(0);

	if (
		completionDate != null &&
		toMoment(completionDate).isBefore(toDate) &&
		toMoment(completionDate).isSameOrAfter(fromDate)
	) {
		toDate = toMoment(completionDate);
	}

	if (!readyForCumulative && hireUnit) {
		const hireItem = new HIIHirePeriod(getId(), true, {
			from: fromDate.toString(),
			to: toDate.toString(),
			hireRate,
			hireDescription: hireDescription ?? '',
			hireType,
			fixtureCurrency: bankAccount.currency,
			useUTC,
			hireDaysDecimals,
			hireUnit,
			useLegacyTime
		});

		hireItem.children.push(
			...(addressCommission ? [new HIIHireAddressCommission(getId(), true, {
				percentage: addressCommission,
				fixtureCurrency: bankAccount.currency,
			})] : []),
			...brokers.map((c) => new HIIHireBrokerCommission(getId(), true, {
				brokerId: c.id,
				brokerName: c.name,
				percentage: c.commission ?? 0,
				fixtureCurrency: bankAccount.currency,
			})),
			...expensesSubjectToHireDays.map((e) => new HIIExpenseSubjectToHireDays(getId(), true, {
				name: e.name,
				amount: e.amount,
				interval: e.interval,
				formulaType: calculationMethodForExpensesSubjectToHireDays ??
					CalculationMethodForExpensesSubjectToHireDays.MONTHLY,
				fixtureCurrency: bankAccount.currency,
				accountCodeOverride: e.accountCodeOverride ?? undefined,
			})),
		);

		items.push(hireItem);
	}

	if (revenuesSubjectToDays != null && revenuesSubjectToDays.length > 0) {
		revenuesSubjectToDays.forEach((r) => {
			items.push(
				new HIIRevenueSubjectToDays(getId(), true, {
					name: r.name,
					amount: r.amount,
					fixtureCurrency: bankAccount.currency,
					startDate: isHireInvoice ? toMoment(fromDate) : undefined,
					endDate: isHireInvoice ? toMoment(toDate) : undefined,
					useUTC,
					accountCodeOverride: r.accountCodeOverride ?? undefined,
				}),
			);
		});
	}

	if (grossBallastBonus != null && ballastBonusAvailable) {
		const ballastBonus = new HIIBallastBonus(getId(), true, {
			amount: grossBallastBonus,
			fixtureCurrency: bankAccount.currency,
		});

		ballastBonus.children.push(
			...(addressCommission ? [new HIIBallastBonusAddressCommission(getId(), true, {
				fixtureCurrency: bankAccount.currency,
			})] : []),
			...brokers.map((c) => new HIIBallastBonusBrokerCommission(getId(), true, {
				brokerId: c.id,
				brokerName: c.name,
				percentage: c.commission,
				fixtureCurrency: bankAccount.currency,
			})),
		);

		items.push(ballastBonus);
	}

	const {
		items: pendingAmendmentItems,
		supersededOriginalIds,
	} = getPendingAmendmentItems(amendments);

	voyageBunkers
		.filter((bunker) => isUninvoicedSourceItem(bunker.HIIBunker?.HireInvoiceItem, supersededOriginalIds))
		.forEach((bunker) => {
			items.push(getBunkerHireInvoiceItem(getId, bunker, bankAccount.currency));
		});

	voyageExpenseReceivables
		.filter((e) => isUninvoicedSourceItem(e.HIIVoyageExpense?.HireInvoiceItem, supersededOriginalIds))
		.forEach((expense) => items.push(getExpenseHireInvoiceItem(
			getId,
			expense,
			bankAccount.currency,
		)));

	voyageOffHirePeriods
		.filter((offHire) => isUninvoicedSourceItem(offHire.HIIOffHirePeriod?.HireInvoiceItem, supersededOriginalIds))
		.forEach((offHire) => {
			items.push(getOffHireHireInvoiceItem(getId, offHire, bankAccount.currency, {
				addressCommission,
				brokers,
				expensesSubjectToHireDays,
				calculationMethodForExpensesSubjectToHireDays: calculationMethodForExpensesSubjectToHireDays ??
				CalculationMethodForExpensesSubjectToHireDays.MONTHLY,
				hireDaysDecimals,
				useUTC,
			}));
		});

	items.push(...pendingAmendmentItems);

	linkChildrenToParents(items);

	return items;
};

export default getAvailableHireInvoiceItems;
